// validation/compareValidation.ts

import { formatDateTime } from "helpers/crf.utils";

export function runCompareValidation(
  sender: any,
  q: any,
  options: any
): boolean {
  // Only run for compare validation
  if (q.validationType !== "compare") return true;

  const v1 = options.value;
  let v2: any;

  // Resolve compare target
  if (q.compareTo) {
    v2 = sender.getValue(q.compareTo);
  } else if (q.compareValue !== undefined) {
    v2 = q.compareValue;
  }

  // Nothing to compare against
  if (v2 === undefined || v2 === null || v2 === "") return true;

  // Normalize values
  const normalize = (v: any): string => {
    if (v instanceof Date) return v.toISOString();
    return String(v);
  };

  const s1 = normalize(v1);
  const s2 = normalize(v2);

  let ok = true;
  const op = q.compareOperator;
  const type = q.inputType || q.getType() || "text";

  // ----------------------------
  // Parsers
  // ----------------------------
  const parseDate = (d: any): Date | null => {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const parseTime = (t: string): number | null => {
    if (!t) return null;

    const m = t.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
    if (!m) return null;

    let h = +m[1];
    const min = +m[2];
    const ap = m[3];

    if (ap) {
      if (ap.toUpperCase() === "PM" && h < 12) h += 12;
      if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    }

    return h * 3600 + min * 60;
  };

  // ----------------------------
  // Comparison logic
  // ----------------------------
  switch (type) {
    case "date":
    case "datetime":
    case "datetime-local": {
      const d1 = parseDate(s1);
      const d2 = parseDate(s2);
      if (!d1 || !d2) return true;

      switch (op) {
        case "=": ok = d1.getTime() === d2.getTime(); break;
        case "!=": ok = d1.getTime() !== d2.getTime(); break;
        case ">": ok = d1 > d2; break;
        case "<": ok = d1 < d2; break;
        case ">=": ok = d1 >= d2; break;
        case "<=": ok = d1 <= d2; break;
      }
      break;
    }

    case "time": {
      const t1 = parseTime(s1);
      const t2 = parseTime(s2);
      if (t1 == null || t2 == null) return true;

      switch (op) {
        case "=": ok = t1 === t2; break;
        case "!=": ok = t1 !== t2; break;
        case ">": ok = t1 > t2; break;
        case "<": ok = t1 < t2; break;
        case ">=": ok = t1 >= t2; break;
        case "<=": ok = t1 <= t2; break;
      }
      break;
    }

    case "password": {
      switch (op) {
        case "=": ok = s1 === s2; break;
        case "!=": ok = s1 !== s2; break;
        default: return true; // unsupported operator
      }
      break;
    }

    default: {
      const n1 = Number(s1);
      const n2 = Number(s2);
      if (Number.isNaN(n1) || Number.isNaN(n2)) return true;

      switch (op) {
        case "=": ok = n1 === n2; break;
        case "!=": ok = n1 !== n2; break;
        case ">": ok = n1 > n2; break;
        case "<": ok = n1 < n2; break;
        case ">=": ok = n1 >= n2; break;
        case "<=": ok = n1 <= n2; break;
      }
    }
  }

  // ----------------------------
  // Error handling
  // ----------------------------
  if (!ok) {
    options.error =
      q.compareError ||
      `Value must be ${op} ${type.includes("datetime") ? formatDateTime(s2) : s2
      }`;
    return false;
  }

  return true;
}
